import { serviceJsonLd, SHARED_STEPS, SHARED_STATS } from "../types";
import type { LandingContent } from "../types";

const faqs = [
  {
    q: "Does the AI give legal advice to callers?",
    a: "No — and that's by design. Our intake agents qualify the matter, capture the facts, run conflict-check routing and book the consultation. They never interpret law or advise callers. Your attorneys stay firmly in control of all legal judgment.",
  },
  {
    q: "Is client data secure and confidential?",
    a: "Yes. Deployments run with audit logs, role-based access controls and encryption in transit and at rest. We design every intake flow around attorney-client confidentiality from day one, and we're happy to sign NDAs and review your compliance requirements before we start.",
  },
  {
    q: "Which practice management tools do you integrate with?",
    a: "Clio, Lawmatics, Filevine, PracticePanther, MyCase and most modern legal CRMs — plus Google Calendar, Outlook, phone systems and intake forms. If your firm runs on something else, we build custom integrations via API.",
  },
  {
    q: "What about Spanish-speaking clients?",
    a: "Our agents are fully bilingual (English/Spanish) with natural, professional phone presence in both languages — a major advantage for firms serving Hispanic communities in Texas and beyond.",
  },
  {
    q: "How much does an AI intake agent cost?",
    a: "Most firms start with a scoped deployment between $8,000 and $20,000 depending on channels (voice, chat, SMS), integrations and call volume. Compare that with a single missed high-value case per month — the math usually closes in weeks, not years.",
  },
  {
    q: "How long until it's live?",
    a: "A standard intake deployment goes live in 4–6 weeks: one week to map your intake process, two for design, and the rest for build, voice training and testing on real call scenarios before launch.",
  },
];

export const lawFirmsContent: LandingContent = {
  seo: {
    title: "AI Agents for Law Firms | 24/7 Intake & Automation — AIdeal Media",
    description:
      "AI agents for law firms: 24/7 AI intake that never misses a call, matter triage, consultation booking and document automation. Free AI readiness assessment.",
    keywords: "ai agents for law firms, ai intake for lawyers, ai receptionist law firm, legal intake automation",
    canonical: "https://www.aidealmedia.info/industries/ai-agents-for-law-firms/",
    ogImage: "https://www.aidealmedia.info/img/hero-bg.jpg",
    jsonLd: serviceJsonLd({
      url: "https://www.aidealmedia.info/industries/ai-agents-for-law-firms/",
      name: "AI Agents for Law Firms",
      description:
        "AI agents for law firms: 24/7 AI-powered intake, matter triage, consultation booking and document review automation.",
      faqs,
    }),
  },
  badge: "Built for solo, small & mid-size firms",
  h1a: "AI Agents for ",
  h1gradient: "Law Firms",
  h1b: "",
  sub: "Your firm misses calls after hours — and every missed call is a client signing with a competitor. Our AI agents answer 24/7, qualify matters and book consultations straight into your calendar.",
  problemTitle: "The first firm to respond wins the client",
  problemIntro:
    "Most people hire the first firm that picks up the phone. If your intake depends on business hours and callbacks, you're donating cases to competitors every single week.",
  pains: [
    {
      icon: "ri-phone-line",
      title: "Missed calls after hours",
      text: "Evenings, weekends, holidays — legal emergencies don't respect office hours. Every unanswered call is a signed retainer walking away.",
    },
    {
      icon: "ri-briefcase-line",
      title: "Partners doing admin, not billable work",
      text: "Intake screening, scheduling ping-pong, data entry into the CMS — none of it bills at partner rates, all of it eats partner time.",
    },
    {
      icon: "ri-timer-line",
      title: "Slow intake leaks clients",
      text: "A prospect who waits hours for a callback has already called three other firms. Speed-to-lead is the whole game in competitive practice areas.",
    },
  ],
  servicesTitle: "AI agents built for how firms actually work",
  servicesIntro:
    "Purpose-built for legal intake and operations. Professional, compliant, and trained on your firm's voice — in English and Spanish.",
  services: [
    {
      icon: "ri-customer-service-2-line",
      title: "24/7 AI Intake Agent",
      text: "Answers every call, captures matter details, qualifies the prospect and books the consultation — nights, weekends and holidays included.",
    },
    {
      icon: "ri-filter-3-line",
      title: "Matter Triage & Routing",
      text: "Every inquiry is classified, scored and routed to the right attorney with a brief — conflict-check workflow included.",
    },
    {
      icon: "ri-calendar-check-line",
      title: "Consultation Booking",
      text: "Real-time calendar sync with confirmations and reminders that slash no-shows.",
    },
    {
      icon: "ri-file-search-line",
      title: "Document Review First-Pass",
      text: "Contracts, discovery batches and correspondence summarized with key terms flagged — your associate validates, not reads.",
    },
    {
      icon: "ri-mail-send-line",
      title: "Follow-Up Automation",
      text: "Prospects who don't book get professional, persistent follow-up sequences until they convert or opt out.",
    },
    {
      icon: "ri-translate-2",
      title: "Bilingual EN/ES Intake",
      text: "Natural Spanish-language intake for firms serving Hispanic communities — a decisive edge in Texas markets.",
    },
  ],
  bandTitle: "Compliance is not an afterthought",
  bandText:
    "Legal work has non-negotiable duties. Every deployment is designed around them:",
  bandPoints: [
    "The AI qualifies and schedules — it never gives legal advice",
    "Attorney-client confidentiality protected with audit logs and access controls",
    "Every conversation logged and reviewable by your team",
    "Clear escalation to a human whenever judgment is required",
  ],
  processTitle: "From audit to automation in 8 weeks",
  processIntro: "A transparent, results-driven process built on measurable KPIs and performance-backed contracts.",
  steps: SHARED_STEPS,
  stats: SHARED_STATS,
  faqTitle: "Frequently asked questions",
  faqs,
  relatedTitle: "Explore more",
  related: [
    {
      href: "/ai-readiness-assessment/",
      label: "Free AI Readiness Assessment",
      desc: "Find your firm's top 3 automation opportunities.",
    },
    {
      href: "/industries/ai-automation-for-logistics/",
      label: "AI Automation for Logistics",
      desc: "Back-office automation for 3PLs and distributors.",
    },
    {
      href: "/ai-automation-agency-houston/",
      label: "AI Automation Agency in Houston",
      desc: "Local partner for Texas businesses.",
    },
  ],
  ctaTitle: "Stop losing clients after 5 PM",
  ctaText:
    "Book a free assessment and see exactly what a 24/7 intake agent would capture for your firm — with projected ROI, in writing, within 24 hours.",
};
